import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, BookOpen } from 'lucide-react';
import { PageHeader, PageHero, PageFooter } from '../components/PageLayout';
import useSEO from '../hooks/useSEO';

const Blog = () => {
  const navigate = useNavigate();
  
  // SEO Meta Tags
  useSEO({
    title: 'Blog - Guides Musique Événements | DJ Nomad',
    description: 'Guides, tutoriels et actualités pour organiser la musique de vos événements. Conseils pour mariages, soirées corporate et fêtes privées.',
    canonical: 'https://djnomad.app/blog',
    ogTitle: 'Blog DJ Nomad - Guides Musique Événements',
    ogDescription: 'Guides et tutoriels pour organiser la musique de vos événements sans DJ.'
  });

  const posts = [
    {
      id: 'dj-without-internet-2025',
      title: 'DJ Without Internet - Complete Guide 2025',
      excerpt: 'Le guide ultime pour mixer en soirée sans connexion Internet. Découvrez comment DJ Nomad révolutionne le DJing avec sa technologie offline-first.',
      date: '17 Décembre 2025',
      readTime: '8 min',
      category: 'Guide',
      image: '/blog/dj-offline-hero.jpg',
      featured: true
    },
    {
      id: 'musique-mariage-sans-dj-2025',
      title: 'Musique de Mariage Sans DJ : Guide Complet 2025',
      excerpt: 'Comment organiser la musique de votre mariage sans engager un DJ professionnel ? Économisez des milliers d\'euros tout en créant une ambiance parfaite.',
      date: '20 Décembre 2025',
      readTime: '10 min',
      category: 'Mariage',
      emoji: '💒',
      featured: false
    },
    {
      id: 'organiser-soiree-entreprise-musique',
      title: 'Comment Organiser la Musique d\'une Soirée d\'Entreprise',
      excerpt: 'Soirées de fin d\'année, team buildings, lancements de produit... Guide complet pour gérer la musique de vos événements corporate.',
      date: '22 Décembre 2025',
      readTime: '7 min',
      category: 'Entreprise',
      emoji: '🏢',
      featured: false
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <PageHeader />

      {/* Hero */}
      <PageHero 
        icon={BookOpen}
        title="Blog DJ Nomad"
        subtitle="Guides, tutoriels et actualités pour organiser la musique de vos événements"
      />

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-12 flex-1">
        {/* Featured Post */}
        {posts.filter(p => p.featured).map(post => (
          <div key={post.id} className="mb-12">
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-[1.01]">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Image */}
                <div className="relative h-64 md:h-auto bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
                  <div className="text-8xl">🎧</div>
                  <div className="absolute top-4 left-4 px-3 py-1 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs font-bold rounded-full">
                    À LA UNE
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-8 flex flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-gray-500">
                    <span className="px-3 py-1 bg-violet-100 border border-violet-200 rounded-full text-violet-600 font-medium">
                      {post.category}
                    </span>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    {post.title}
                  </h2>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {post.excerpt}
                  </p>
                  
                  <button
                    onClick={() => navigate(`/blog/${post.id}`)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 self-start"
                  >
                    Lire l&apos;article
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Other Articles */}
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Tous nos articles</h2>
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {posts.filter(p => !p.featured).map(post => (
            <article 
              key={post.id}
              onClick={() => navigate(`/blog/${post.id}`)}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-[1.02] cursor-pointer"
            >
              {/* Emoji Header */}
              <div className="h-32 bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center">
                <span className="text-6xl">{post.emoji}</span>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="flex flex-wrap items-center gap-3 mb-3 text-sm text-gray-500">
                  <span className="px-2 py-1 bg-violet-100 border border-violet-200 rounded-full text-violet-600 font-medium text-xs">
                    {post.category}
                  </span>
                  <div className="flex items-center gap-1 text-xs">
                    <Calendar className="w-3 h-3" />
                    <span>{post.date}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <Clock className="w-3 h-3" />
                    <span>{post.readTime}</span>
                  </div>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {post.title}
                </h3>
                
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center gap-2 text-violet-600 font-semibold text-sm">
                  Lire l&apos;article
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Newsletter CTA */}
        <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-2xl p-8 text-center">
          <div className="text-5xl mb-4">📬</div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Restez informé</h3>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            Nous publions régulièrement des guides et tutoriels pour vous aider à organiser 
            la musique de vos événements. Revenez bientôt pour découvrir nos nouveaux articles !
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-300"
          >
            Essayer DJ Nomad Gratuitement
          </button>
        </div>
      </div>

      {/* Footer */}
      <PageFooter />
    </div>
  );
};

export default Blog;
